import { useMutation } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { formatMb } from '@/lib/utils'

/**
 * Upload rules from spec Requirement 7, capped to 4 MB rather than the
 * spec'd 5 — Hertz's own default MaxRequestBodySize is 4 MB and nothing
 * raises it, so the server never actually accepts more than that. The
 * server is the authority — it validates by sniffing the actual bytes, not
 * the declared type — but the same limits are checked here so an operator
 * gets an instant answer instead of a round trip. Both sides read these
 * constants so they cannot drift.
 */
export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const
export const MAX_IMAGE_BYTES = 4 * 1024 * 1024

/** Where the object lands: `ops/banner/…` or `ops/trending/…`. */
export type UploadPurpose = 'banner' | 'trending'

/** Returns a human-readable reason, or null when the file is acceptable. */
export function checkImage(file: File): string | null {
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type as (typeof ACCEPTED_IMAGE_TYPES)[number])) {
    return 'Use a JPEG, PNG, or WebP image.'
  }
  if (file.size > MAX_IMAGE_BYTES) {
    return `That file is ${formatMb(file.size)} MB — the limit is 4 MB.`
  }
  return null
}

/**
 * POST /admin/uploads. Returns the stored URL, which the caller saves on the
 * banner or trending record; the database never holds binary data.
 */
export function useUploadImage() {
  return useMutation({
    mutationFn: async ({ file, purpose }: { file: File; purpose: UploadPurpose }) => {
      const form = new FormData()
      form.append('file', file)
      form.append('purpose', purpose)
      const { url } = await api.post<{ url: string }>('/admin/uploads', form)
      return url
    },
  })
}
